import { useEffect, useState } from "react";

const KEY = "nr_theme";

// ── Theme change pub/sub ────────────────────────────────────────────────
// Lets non-React consumers (e.g. the MapLibre map) re-skin when the theme
// flips, without prop-drilling. Fires with the new theme string.
const themeListeners = new Set();
export function onThemeChange(fn) {
  themeListeners.add(fn);
  return () => themeListeners.delete(fn);
}
function emitThemeChange(next) {
  themeListeners.forEach((fn) => {
    try { fn(next); } catch (_) {}
  });
}

function readTheme() {
  const saved = localStorage.getItem(KEY);
  // Default to DARK. The site's identity is the dark forest theme, so it
  // should always open dark regardless of the visitor's OS light/dark
  // setting. Light is opt-in via the toggle and remembered once chosen.
  return saved === "light" || saved === "dark" ? saved : "dark";
}

// Applies the theme to <html>, syncs the browser chrome color, and (unless
// `instant`) plays a short cross-fade so the flip feels intentional.
function applyTheme(next, { instant = false } = {}) {
  const root = document.documentElement;
  if (!instant) {
    root.classList.add("theme-switching");
    window.clearTimeout(applyTheme._t);
    applyTheme._t = window.setTimeout(
      () => root.classList.remove("theme-switching"),
      520
    );
  }
  root.setAttribute("data-theme", next);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", next === "light" ? "#f7efdb" : "#05140b");
}

// Called once at app start (kept for API compatibility with existing imports).
export function initTheme() {
  const t = readTheme();
  applyTheme(t, { instant: true });
  return t;
}

const SunIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

export default function ThemeToggle({ className = "" }) {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "dark"
  );

  useEffect(() => {
    setTheme(document.documentElement.getAttribute("data-theme") || readTheme());
  }, []);

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    applyTheme(next);
    try { localStorage.setItem(KEY, next); } catch (_) {}
    emitThemeChange(next);
  };

  const isDark = theme === "dark";

  return (
    <button
      className={`theme-toggle ${className}`}
      onClick={toggle}
      role="switch"
      aria-checked={!isDark}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      title={`Switch to ${isDark ? "light" : "dark"} mode`}
    >
      <span className="theme-toggle__rail-icon theme-toggle__rail-icon--moon" aria-hidden="true">
        <MoonIcon />
      </span>
      <span className="theme-toggle__rail-icon theme-toggle__rail-icon--sun" aria-hidden="true">
        <SunIcon />
      </span>
      <span className="theme-toggle__thumb" aria-hidden="true">
        {isDark ? <MoonIcon /> : <SunIcon />}
      </span>
    </button>
  );
}
